import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import RecipientService from '../../../../services/RecipientService';
import AuthService from '../../../../services/AuthService';
import Spinner from 'react-bootstrap/Spinner';
import RecipientCreateModal from './RecipientCreateModal';

const Step3Form = ({ formData, handleChange, onNext, onBack }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRecipient, setSelectedRecipient] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const fetchRecipients = async () => {
        try {
            const currentUser = AuthService.getCurrentUser();
            if (currentUser) {
                const response = await RecipientService.getAllRecipients();
                // Filtre 1: Destinataires de l'utilisateur connecté
                const userRecipients = response.filter(recipient =>
                    recipient.createdBy === currentUser.username
                );
                // Filtre 2: Destinataires du pays sélectionné à l'étape 1
                const countryFilteredRecipients = userRecipients.filter(recipient =>
                    recipient.country === formData.country
                );
                setRecipients(countryFilteredRecipients);
            }
        } catch (error) {
            console.error("Error fetching recipients:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRecipients();
    }, [formData.country]);

    // Gestionnaire pour le rafraîchissement après création
    const handleCreateSuccess = async () => {
        setShowCreateModal(false); // Fermer la modal de création
        setLoading(true); // Afficher le spinner
        await fetchRecipients(); // Recharger les destinataires
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleRecipientClick = (recipient) => {
        setSelectedRecipient(recipient);
        setShowModal(true);
        handleChange({ target: { name: 'recipientId', value: recipient.id || "" } });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAccount(null);
    };

    const handleAccountSelect = (account, type) => {
        setSelectedAccount(account);

        if (type === 'bank') {
            handleChange({ target: { name: 'bankAccountNumber', value: account.accountNumber || "" } });
            handleChange({ target: { name: 'recipientBankAccountInfo', value: account.id } });
        } else if (type === 'wallet') {
            handleChange({ target: { name: 'walletAccountNumber', value: account.phoneNumber } });
            handleChange({ target: { name: 'recipientWalletAccountInfo', value: account.id } });
        }
    };

    // Filtre 3 et 4 combinés: Filtrage des comptes en fonction de la méthode de retrait
    const getFilteredAccounts = () => {
        if (!selectedRecipient) return [];

        const isBankMethod = formData.withdrawalMethod === 'BANK';
        const isWaveMethod = formData.withdrawalMethod === 'WAVE';

        if (isBankMethod) {
            return selectedRecipient.recipientAccountInfo.bankAccountInfos;
        } else if (isWaveMethod) {
            // Pour WAVE, on utilise les comptes Wave spécifiques
            const walletAccounts = selectedRecipient.recipientAccountInfo.walletAccountInfos;

            // Filtrer les comptes Wave
            const waveAccounts = walletAccounts.filter(account =>
                account.walletType === 'WAVE'
            );

            ////console.log('Comptes Wave filtrés:', waveAccounts);
            return waveAccounts;
        } else {
            // Pour les autres méthodes mobile money (MTN, Orange)
            const walletAccounts = selectedRecipient.recipientAccountInfo.walletAccountInfos;

            // Mapping pour les types de wallet
            const walletTypeMap = {
                'MOBILE MONEY': 'MTN_MOBILE_MONEY',
                'Orange Money': 'ORANGE_MONEY',
                'WAVE': 'WAVE'
            };

            ////console.log('Méthode de retrait sélectionnée:', formData.withdrawalMethod);
            const selectedWalletType = walletTypeMap[formData.withdrawalMethod];
            ////console.log('Type de wallet correspondant:', selectedWalletType);

            const filteredWallets = walletAccounts.filter(account =>
                account.walletType === selectedWalletType
            );

            //console.log('Comptes wallet filtrés:', filteredWallets);
            return filteredWallets;
        }
    };

    // Remplacer le Link par un Button
    const addRecipientButton = (
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            Ajouter un destinataire
        </Button>
    );

    // Filtre par recherche de nom
    const filteredRecipients = recipients.filter(recipient =>
        recipient.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recipient.lastName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredAccounts = getFilteredAccounts();

    // Déterminer le type de compte à afficher
    const getAccountTypeLabel = () => {
        switch (formData.withdrawalMethod) {
            case 'BANK':
                return 'Comptes bancaires';
            case 'WAVE':
                return 'Comptes Wave';
            case 'MOBILE MONEY':
                return 'Comptes MTN Mobile Money';
            case 'Orange Money':
                return 'Comptes Orange Money';
            default:
                return 'Comptes de portefeuille';
        }
    };

    return (
        <div className="step3-form">
            <div className="form-actions mb-3" style={{display: 'flex', justifyContent: 'space-between'}}>
                <Button variant="secondary" onClick={onBack}>
                    Retour
                </Button>
                {addRecipientButton}
            </div>

            <Form.Group controlId="search" className="mb-4">
                <div style={{position: 'relative'}}>
                    <Form.Control
                        type="text"
                        placeholder="Rechercher un destinataire"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="search-input"
                        style={{paddingLeft: '30px', paddingRight: '30px'}}
                    />
                    <i className="fas fa-search" style={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#ccc'
                    }}></i>
                    {searchTerm && (
                        <i
                            className="fas fa-times"
                            style={{
                                position: 'absolute',
                                right: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                cursor: 'pointer',
                                color: '#ccc'
                            }}
                            onClick={() => setSearchTerm('')}
                        ></i>
                    )}
                </div>
            </Form.Group>

            {loading ? (
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </Spinner>
                </div>
            ) : (
                <div className="verify-number-content">
                    <div className="transfer-to-bank">
                        {filteredRecipients.length > 0 ? (
                            filteredRecipients.map(recipient => (
                                <div
                                    key={recipient.id}
                                    className="send-money-contact-tab request-money1 p-1 mb-2"
                                    onClick={() => handleRecipientClick(recipient)}
                                    style={{cursor: 'pointer'}}
                                >
                                    <div className="contact-profile">
                                        <img src="assets/images/invite-friend/avatar.jpg" alt="profile"
                                             style={{height: '80px', width: '80px', borderRadius: '50%'}}/>
                                    </div>
                                    <div className="contact-details" style={{flex: 1, minWidth: 0}}>
                                        <h3 style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {recipient.firstName} {recipient.lastName}
                                        </h3>
                                        <h4 style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {recipient.email}
                                        </h4>
                                        <h4 style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {recipient.country}
                                        </h4>
                                    </div>
                                    <div className="contact-star">
                                        <div className="star-favourite"></div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                <img
                                    src="assets/images/setting/no_data.png"
                                    alt="Aucun transfert"
                                    className="mb-3"
                                    style={{width: '100px', opacity: 0.7}}
                                />
                                <p className="text-muted">Aucun destinataire trouvé pour ce pays ou ces critères.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <Modal
                show={showModal}
                onHide={handleCloseModal}
                className="recipient-modal"
                centered
                size="lg"
            >
                <Modal.Header closeButton className="border-0 bg-light">
                    <Modal.Title className="fw-bold text-primary">
                        <i className="bi bi-person-badge me-2"></i>
                        Détails du destinataire
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="py-3">
                    {selectedRecipient && (
                        <div className="mb-4">
                            <div className="row g-3 mb-4">
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded">
                                        <h6 className="text-muted small mb-2">Nom complet</h6>
                                        <p className="mb-0 fw-bold text-truncate">
                                            {selectedRecipient.firstName} {selectedRecipient.lastName}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded">
                                        <h6 className="text-muted small mb-2">Email</h6>
                                        <p className="mb-0 fw-bold text-truncate">
                                            {selectedRecipient.email}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded">
                                        <h6 className="text-muted small mb-2">Pays</h6>
                                        <p className="mb-0 fw-bold text-truncate">
                                            {selectedRecipient.country}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="p-3 bg-light rounded">
                                        <h6 className="text-muted small mb-2">Ville</h6>
                                        <p className="mb-0 fw-bold text-truncate">
                                            {selectedRecipient.city}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {filteredAccounts.length > 0 ? (
                                <>
                                    <h5 className="mb-3 fw-bold">
                                        <i className="bi bi-credit-card me-2"></i>
                                        {getAccountTypeLabel()}
                                    </h5>

                                    <div className="account-list">
                                        {filteredAccounts.map(account => (
                                            <div key={account.id} className="account-item card mb-3 border-primary">
                                                <div className="card-body">
                                                    <Form.Check
                                                        type="radio"
                                                        id={`account-${account.id}`}
                                                        className="form-check-lg"
                                                        label={
                                                            formData.withdrawalMethod === 'BANK' ? (
                                                                <div className="ms-3">
                                                                    <h6 className="fw-bold text-primary">
                                                                        <i className="bi bi-bank me-2"></i>
                                                                        Compte bancaire
                                                                    </h6>
                                                                    <div className="row g-2 mt-2">
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1">
                                                                                <span className="text-muted small">Intitulé:</span><br/>
                                                                                <span className="fw-bold text-truncate d-block">
                                                                                    {account.accountName}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1">
                                                                                <span className="text-muted small">Banque:</span><br/>
                                                                                <span className="fw-bold text-truncate d-block">
                                                                                    {account.branchName}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1">
                                                                                <span className="text-muted small">Numéro:</span><br/>
                                                                                <span className="fw-bold text-truncate d-block">
                                                                                    {account.accountNumber}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1">
                                                                                <span className="text-muted small">Succursale:</span><br/>
                                                                                <span className="fw-bold text-truncate d-block">
                                                                                    {account.branchNumber}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="ms-3">
                                                                    <h6 className="fw-bold text-primary">
                                                                        <i className="bi bi-wallet2 me-2"></i>
                                                                        {formData.withdrawalMethod === 'WAVE' ? 'Compte Wave' : 'Compte de portefeuille'}
                                                                    </h6>
                                                                    <div className="row g-2 mt-2">
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1">
                                                                                <span className="text-muted small">Type:</span><br/>
                                                                                <span className="fw-bold text-truncate d-block">
                                                                                    {account.walletType}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1">
                                                                                <span className="text-muted small">Nom:</span><br/>
                                                                                <span className="fw-bold text-truncate d-block">
                                                                                    {account.accountName}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1">
                                                                                <span className="text-muted small">Téléphone:</span><br/>
                                                                                <span className="fw-bold text-truncate d-block">
                                                                                    {account.phoneNumber}
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        }
                                                        checked={selectedAccount?.id === account.id}
                                                        onChange={() => handleAccountSelect(
                                                            account,
                                                            formData.withdrawalMethod === 'BANK' ? 'bank' : 'wallet'
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="alert alert-info">
                                    <i className="bi bi-info-circle me-2"></i>
                                    Aucun compte {formData.withdrawalMethod === 'BANK' ? 'bancaire' :
                                    formData.withdrawalMethod === 'WAVE' ? 'Wave' : 'de portefeuille'} disponible pour ce destinataire.
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="border-0">
                    <Button variant="outline-secondary" onClick={handleCloseModal}>
                        <i className="bi bi-x-circle me-2"></i>
                        Fermer
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            onNext(selectedRecipient, selectedAccount);
                            handleCloseModal();
                        }}
                        disabled={!selectedAccount}
                        className="d-flex align-items-center"
                    >
                        <i className="bi bi-arrow-right-circle me-2"></i>
                        Suivant
                    </Button>
                </Modal.Footer>
            </Modal>

            <RecipientCreateModal
                show={showCreateModal}
                handleClose={() => setShowCreateModal(false)}
                onDataReload={handleCreateSuccess}
            />
        </div>
    );
};

export default Step3Form;